/**
 * Parasite Rate Service
 *
 * Frontend service for the parasite rate data feature. Provides methods for
 * calling the parasite rate API endpoints via the API Gateway:
 *   GET /api/parasite-rate/countries              → list available country IDs
 *   GET /api/parasite-rate/countries/{id}         → get data for a specific country
 *   GET /api/parasite-rate/countries/{id}/coverage → get source-type coverage counts for a country
 *   GET /api/parasite-rate/source-ids             → list distinct source_id1 values
 *   GET /api/parasite-rate/validation-failures    → get validation failure records
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * A single parasite rate data point containing all columns from the backend
 * parquet dataset. Each field maps directly to a column in the data.
 */
export interface ParasiteRatePoint {
  country: string | null
  country_id: string | null
  cluster_id: string | null
  site_name: string | null
  dhs_survey_num: string | null
  dhs_survey_id: string | null
  dhs_id: string | null
  latitude: number | null
  longitude: number | null
  rural_urban_type: string | null
  extracted_admin_1_name: string | null
  admin1_dhs: string | null
  dhs_region_code: string | null
  dhs_region_name: string | null
  lat_long_source: string | null
  area_type: string | null
  area_size: string | null
  is_school: string | null
  is_excluded: string | null
  exclusion_reason: string | null
  survey_notes: string | null
  site_notes: string | null
  pcr_type: string | null
  rdt_type: string | null
  micro_type: string | null
  method: string | null
  household_sample_weight: number | null
  month_start: number | null
  month_end: number | null
  day_start: number | null
  day_end: number | null
  year_start: number | null
  year_end: number | null
  lower_age_months_rounded: number | null
  upper_age_months_rounded: number | null
  examined: number | null
  any_positive_count: number | null
  any_positive_rate: number | null
  pf_positive_count: number | null
  pf_positive_rate: number | null
  computed_total_pf_positive_count: number | null
  computed_total_pf_positive_rate: number | null
  pv_positive_count: number | null
  pv_positive_rate: number | null
  computed_total_pv_positive_count: number | null
  computed_total_pv_positive_rate: number | null
  pk_positive_count: number | null
  pk_positive_rate: number | null
  computed_total_pk_positive_count: number | null
  computed_total_pk_positive_rate: number | null
  po_positive_count: number | null
  po_positive_rate: number | null
  computed_total_po_positive_count: number | null
  computed_total_po_positive_rate: number | null
  pm_positive_count: number | null
  pm_positive_rate: number | null
  computed_total_pm_positive_count: number | null
  computed_total_pm_positive_rate: number | null
  pan_positive_count: number | null
  pan_positive_rate: number | null
  pf_pv_positive_count: number | null
  pf_pv_positive_rate: number | null
  pf_po_positive_count: number | null
  pf_po_positive_rate: number | null
  pf_pm_positive_count: number | null
  pf_pm_positive_rate: number | null
  pv_pm_positive_count: number | null
  pv_pm_positive_rate: number | null
  pv_po_positive_count: number | null
  pv_po_positive_rate: number | null
  po_pm_positive_count: number | null
  po_pm_positive_rate: number | null
  pk_pf_positive_count: number | null
  pk_pf_positive_rate: number | null
  pk_pv_positive_count: number | null
  pk_pv_positive_rate: number | null
  pk_pm_positive_count: number | null
  pk_pm_positive_rate: number | null
  pk_po_positive_count: number | null
  pk_po_positive_rate: number | null
  pf_pv_po_positive_count: number | null
  pf_pv_po_positive_rate: number | null
  pf_pv_pm_positive_count: number | null
  pf_pv_pm_positive_rate: number | null
  pf_po_pm_positive_count: number | null
  pf_po_pm_positive_rate: number | null
  pv_po_pm_positive_count: number | null
  pv_po_pm_positive_rate: number | null
  pf_pv_pk_positive_count: number | null
  pf_pv_pk_positive_rate: number | null
  pf_po_pk_positive_count: number | null
  pf_po_pk_positive_rate: number | null
  pf_pm_pk_positive_count: number | null
  pf_pm_pk_positive_rate: number | null
  pv_po_pk_positive_count: number | null
  pv_po_pk_positive_rate: number | null
  pv_pm_pk_positive_count: number | null
  pv_pm_pk_positive_rate: number | null
  po_pm_pk_positive_count: number | null
  po_pm_pk_positive_rate: number | null
  pf_pv_po_pm_positive_count: number | null
  pf_pv_po_pm_positive_rate: number | null
  pf_pan_positive_count: number | null
  pf_pan_positive_rate: number | null
  pv_pan_positive_count: number | null
  pv_pan_positive_rate: number | null
  pf_pv_pan_positive_count: number | null
  pf_pv_pan_positive_rate: number | null
  map_survey_id: string | null
  lit_review_id: string | null
  extracted_admin_2_name: string | null
  extracted_admin_3_name: string | null
  source_id1: string | null
  source_id2: string | null
  source_id3: string | null
}

/**
 * The logical column type ("number" or "string") for each `ParasiteRatePoint`
 * field, used when loading the points into a typed store (e.g. the in-browser
 * DuckDB query engine) so each column gets the correct type even when every
 * loaded value happens to be null.
 */

export const PARASITE_RATE_COLUMN_TYPES: Record<keyof ParasiteRatePoint, "number" | "string"> = {
  country: "string",
  country_id: "string",
  cluster_id: "string",
  site_name: "string",
  dhs_survey_num: "string",
  dhs_survey_id: "string",
  dhs_id: "string",
  latitude: "number",
  longitude: "number",
  rural_urban_type: "string",
  extracted_admin_1_name: "string",
  admin1_dhs: "string",
  dhs_region_code: "string",
  dhs_region_name: "string",
  lat_long_source: "string",
  area_type: "string",
  area_size: "string",
  is_school: "string",
  is_excluded: "string",
  exclusion_reason: "string",
  survey_notes: "string",
  site_notes: "string",
  pcr_type: "string",
  rdt_type: "string",
  micro_type: "string",
  method: "string",
  household_sample_weight: "number",
  month_start: "number",
  month_end: "number",
  day_start: "number",
  day_end: "number",
  year_start: "number",
  year_end: "number",
  lower_age_months_rounded: "number",
  upper_age_months_rounded: "number",
  examined: "number",
  any_positive_count: "number",
  any_positive_rate: "number",
  pf_positive_count: "number",
  pf_positive_rate: "number",
  computed_total_pf_positive_count: "number",
  computed_total_pf_positive_rate: "number",
  pv_positive_count: "number",
  pv_positive_rate: "number",
  computed_total_pv_positive_count: "number",
  computed_total_pv_positive_rate: "number",
  pk_positive_count: "number",
  pk_positive_rate: "number",
  computed_total_pk_positive_count: "number",
  computed_total_pk_positive_rate: "number",
  po_positive_count: "number",
  po_positive_rate: "number",
  computed_total_po_positive_count: "number",
  computed_total_po_positive_rate: "number",
  pm_positive_count: "number",
  pm_positive_rate: "number",
  computed_total_pm_positive_count: "number",
  computed_total_pm_positive_rate: "number",
  pan_positive_count: "number",
  pan_positive_rate: "number",
  pf_pv_positive_count: "number",
  pf_pv_positive_rate: "number",
  pf_po_positive_count: "number",
  pf_po_positive_rate: "number",
  pf_pm_positive_count: "number",
  pf_pm_positive_rate: "number",
  pv_pm_positive_count: "number",
  pv_pm_positive_rate: "number",
  pv_po_positive_count: "number",
  pv_po_positive_rate: "number",
  po_pm_positive_count: "number",
  po_pm_positive_rate: "number",
  pk_pf_positive_count: "number",
  pk_pf_positive_rate: "number",
  pk_pv_positive_count: "number",
  pk_pv_positive_rate: "number",
  pk_pm_positive_count: "number",
  pk_pm_positive_rate: "number",
  pk_po_positive_count: "number",
  pk_po_positive_rate: "number",
  pf_pv_po_positive_count: "number",
  pf_pv_po_positive_rate: "number",
  pf_pv_pm_positive_count: "number",
  pf_pv_pm_positive_rate: "number",
  pf_po_pm_positive_count: "number",
  pf_po_pm_positive_rate: "number",
  pv_po_pm_positive_count: "number",
  pv_po_pm_positive_rate: "number",
  pf_pv_pk_positive_count: "number",
  pf_pv_pk_positive_rate: "number",
  pf_po_pk_positive_count: "number",
  pf_po_pk_positive_rate: "number",
  pf_pm_pk_positive_count: "number",
  pf_pm_pk_positive_rate: "number",
  pv_po_pk_positive_count: "number",
  pv_po_pk_positive_rate: "number",
  pv_pm_pk_positive_count: "number",
  pv_pm_pk_positive_rate: "number",
  po_pm_pk_positive_count: "number",
  po_pm_pk_positive_rate: "number",
  pf_pv_po_pm_positive_count: "number",
  pf_pv_po_pm_positive_rate: "number",
  pf_pan_positive_count: "number",
  pf_pan_positive_rate: "number",
  pv_pan_positive_count: "number",
  pv_pan_positive_rate: "number",
  pf_pv_pan_positive_count: "number",
  pf_pv_pan_positive_rate: "number",
  map_survey_id: "string",
  lit_review_id: "string",
  extracted_admin_2_name: "string",
  extracted_admin_3_name: "string",
  source_id1: "string",
  source_id2: "string",
  source_id3: "string",
}

/**
 * A validation failure record. Contains all fields from ParasiteRatePoint
 * plus a `validation_error` column describing what failed validation.
 */
export interface ValidationFailurePoint extends ParasiteRatePoint {
  validation_error: string | null
}

/**
 * Response shape from GET /api/parasite-rate/countries.
 */
interface CountriesResponse {
  /** The resolved dataset version. */
  version: string
  /** Array of ISO3 country IDs with available data. */
  countries: string[]
}

/**
 * Response shape from GET /api/parasite-rate/versions.
 */
interface VersionsResponse {
  /** The latest (most recent) version string (e.g. "v2.3"). */
  latest_version: string
  /** All available version strings sorted descending (e.g. ["v2.3", "v2.2", "v1.0", "v0.0"]). */
  versions: string[]
}

/**
 * Response shape from GET /api/parasite-rate/source-ids.
 */
interface SourceIdsResponse {
  /** The resolved dataset version. */
  version: string
  /** Distinct source_id1 values present in the dataset, sorted ascending. */
  source_ids: string[]
}

/**
 * Response shape from GET /api/parasite-rate/countries/{countryId}.
 * Data is column-oriented (each key maps to an array of values).
 */
interface CountryDataResponse {
  /** The resolved dataset version. */
  version: string
  /** The country ID that was requested. */
  country_id: string
  /** Number of rows in the data. */
  row_count: number
  /** Column-oriented data (column_name → values[]). */
  data: Record<string, (string | number | null)[]>
}

/**
 * A year→count map for one source category. Keys are the start year rendered as
 * a string (e.g. "2015"); values are how many data points fall in that year.
 */
export type CoverageByYear = Record<string, number>

/**
 * Response shape from GET /api/parasite-rate/countries/{countryId}/coverage.
 *
 * Counts are per unique location, not per raw row: the backend deduplicates on
 * (latitude, longitude, source_id1, year_start) before counting
 */
export interface CountryCoverageResponse {
  /** The resolved dataset version. */
  version: string
  /** The country ID that was requested. */
  country_id: string
  /** Start-year → count of survey data points. */
  surveys: CoverageByYear
  /** Start-year → count of all non-survey (literature review) data points. */
  literature_review: CoverageByYear
}

// ---------------------------------------------------------------------------
// API Base URL
// ---------------------------------------------------------------------------

/** Cached API base URL loaded from aws-exports.json. */
let PARASITE_RATE_API_URL: string = ""

/**
 * Load the parasite rate API base URL from aws-exports.json configuration.
 *
 * Uses VITE_API_URL env var if set, otherwise falls back to the apiUrl from
 * aws-exports.json, and finally to a relative path.
 *
 * @returns The base URL for the parasite rate API (e.g. "https://host/api/parasite-rate").
 */
async function loadApiUrl(): Promise<string> {
  if (PARASITE_RATE_API_URL) {
    return PARASITE_RATE_API_URL
  }

  // VITE_API_URL is available at build time in deployed mode — use it
  // directly without needing to fetch aws-exports.json.
  const envUrl: string = import.meta.env.VITE_API_URL || ""
  if (envUrl) {
    PARASITE_RATE_API_URL = `${envUrl}/api/parasite-rate`
    return PARASITE_RATE_API_URL
  }

  // Fall back to aws-exports.json for CI-built deployments where the
  // API URL is baked into the static config file.
  try {
    const response = await fetch("/aws-exports.json")
    const config = await response.json()
    const baseUrl: string = config.apiUrl || ""
    PARASITE_RATE_API_URL = baseUrl
      ? `${baseUrl}/api/parasite-rate`
      : "/api/parasite-rate"
    return PARASITE_RATE_API_URL
  } catch (error) {
    console.error("Failed to load API URL from aws-exports.json:", error)
    throw new Error("Parasite Rate API URL not configured")
  }
}

// ---------------------------------------------------------------------------
// API Methods
// ---------------------------------------------------------------------------

/**
 * Fetch the list of country IDs that have parasite rate data for the most
 * recent dataset version (no version parameter = latest).
 *
 * @param token - Auth0 access token for API authentication.
 * @returns The countries response including version and country ID array.
 * @throws Error if the API call fails.
 */
/**
 * Fetch the list of country IDs that have parasite rate data for a specific
 * dataset version.
 *
 * @param token - Auth0 access token for API authentication.
 * @param version - Optional dataset version to query (e.g. "v2.3"). If omitted, the backend returns the latest.
 * @returns The countries response including version and country ID array.
 * @throws Error if the API call fails.
 */
export async function getCountries(token: string, version?: string): Promise<CountriesResponse> {
  const apiUrl = await loadApiUrl()

  const url = version
    ? `${apiUrl}/countries?version=${encodeURIComponent(version)}`
    : `${apiUrl}/countries`
  const response = await fetch(url, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.error || `Failed to fetch countries: HTTP ${response.status}`)
  }

  return response.json()
}

/**
 * Fetch all available dataset versions. Returns every version in the format
 * vX.Y sorted descending (newest first), plus the currently active version.
 * Filtering to the current major version is done on the frontend.
 *
 * @param token - Auth0 access token for API authentication.
 * @returns The versions response including current version and full list of available versions.
 * @throws Error if the API call fails.
 */
export async function getVersions(token: string): Promise<VersionsResponse> {
  const apiUrl = await loadApiUrl()

  const response = await fetch(`${apiUrl}/versions`, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.error || `Failed to fetch versions: HTTP ${response.status}`)
  }

  return response.json()
}

/**
 * The resolved result of a source-ids fetch: the version the backend actually
 * served and the distinct set of source IDs present in it.
 */
interface SourceIdsResult {
  /** The dataset version the backend resolved and served. */
  version: string
  /** Distinct source_id1 values present in that version. */
  sourceIds: Set<string>
}

/**
 * In-memory cache for source-ids results, keyed by the *requested* version.
 */
const SOURCE_IDS_LATEST_KEY = "__latest__"
const _sourceIdsCache: Map<string, SourceIdsResult> = new Map()

/**
 * In-flight source-ids requests, keyed the same way as _sourceIdsCache. Lets
 * concurrent callers (e.g. two components mounting at once) share a single
 * network request instead of each firing their own scan.
 */
const _sourceIdsInFlight: Map<string, Promise<SourceIdsResult>> = new Map()

/**
 * Perform the actual source-ids network request (no caching). Separated from
 * the cached entry point so the caching layer can wrap it cleanly.
 *
 * @param token - Auth0 access token for API authentication.
 * @param version - Optional dataset version to query, or undefined for latest.
 * @returns The resolved version and the distinct set of source IDs.
 * @throws Error if the API call fails.
 */
async function fetchParasiteRateSourceIds(
  token: string,
  version?: string,
): Promise<SourceIdsResult> {
  const apiUrl = await loadApiUrl()

  const url = version
    ? `${apiUrl}/source-ids?version=${encodeURIComponent(version)}`
    : `${apiUrl}/source-ids`
  const response = await fetch(url, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.error || `Failed to fetch source IDs: HTTP ${response.status}`)
  }

  const data: SourceIdsResponse = await response.json()
  return { version: data.version, sourceIds: new Set(data.source_ids) }
}

/**
 * Fetch the distinct set of source_id1 values present in the parasite rate
 * dataset for a given version, with process-lifetime caching and in-flight
 * request de-duplication.
 *
 * Results are cached per requested version for the life of the page.
 *
 * @param token - Auth0 access token for API authentication.
 * @param version - Optional dataset version to query (e.g. "v2.3"). If omitted, the backend returns the latest.
 * @returns The resolved version and the set of distinct source IDs.
 * @throws Error if the API call fails and no cached value is available.
 */
export async function getParasiteRateSourceIds(
  token: string,
  version?: string,
): Promise<SourceIdsResult> {
  // Key on what the caller requested so repeated "latest" calls share an entry.
  const cacheKey: string = version ?? SOURCE_IDS_LATEST_KEY

  // 1. Serve a completed result straight from cache.
  const cached: SourceIdsResult | undefined = _sourceIdsCache.get(cacheKey)
  if (cached != null) {
    return cached
  }

  // 2. Join an identical request already in flight rather than starting another.
  const inFlight: Promise<SourceIdsResult> | undefined = _sourceIdsInFlight.get(cacheKey)
  if (inFlight != null) {
    return inFlight
  }

  // 3. No cache hit and nothing in flight — start the request, record it as
  //    in-flight, and populate the cache on success. The in-flight entry is
  //    cleared in all cases so a failure does not wedge future calls.
  const request: Promise<SourceIdsResult> = fetchParasiteRateSourceIds(token, version)
    .then((result) => {
      _sourceIdsCache.set(cacheKey, result)
      return result
    })
    .finally(() => {
      _sourceIdsInFlight.delete(cacheKey)
    })

  _sourceIdsInFlight.set(cacheKey, request)
  return request
}

/**
 * The major dataset version the UI schema currently supports.
 *
 * Bump this when the frontend gains support for a new major (e.g. the next
 * schema). Until then, data published under a higher major is intentionally
 * hidden from the version selector.
 */
const SUPPORTED_MAJOR_VERSION: number = 3

/**
 * Parse the major version integer out of a "vX.Y" (or legacy "vX") version
 * string.
 *
 * @param version - A version string such as "v2.3" or "v2".
 * @returns The major version as a number (e.g. 2), or null if the string does
 *   not start with the expected "v<digits>" pattern.
 */
function parseMajorVersion(version: string): number | null {
  const major: string | undefined = version.match(/^v(\d+)/)?.[1]
  return major != null ? Number(major) : null
}

/**
 * Filter a list of versions to those within a given major version — e.g. with
 * a major of 2, keeps "v2.x" and drops "v1.x" and "v3.x". Different majors may
 * use a different schema, so the app only ever exposes one major at a time.
 *
 * @param versions - All available version strings (e.g. ["v2.3", "v1.4"]).
 * @param major - The major version to keep. Defaults to the UI's supported
 *   major (SUPPORTED_MAJOR_VERSION); pass an explicit value only to target a
 *   different major.
 * @returns The subset of versions whose major equals the given major.
 */
export function getVersionsForMajor(
  { versions, major = SUPPORTED_MAJOR_VERSION }: { versions: string[]; major?: number },
): string[] {
  // Keep only versions whose leading major integer matches the target major.
  return versions.filter((version) => parseMajorVersion(version) === major)
}

/**
 * Parse the minor version integer out of a "vX.Y" version string. Legacy "vX"
 * strings (no minor component) are treated as minor 0.
 *
 * @param version - A version string such as "v2.3" or "v2".
 * @returns The minor version as a number (e.g. 3), or null if the string does
 *   not match the expected "v<digits>(.<digits>)?" pattern.
 */
function parseMinorVersion(version: string): number | null {
  const match: RegExpMatchArray | null = version.match(/^v\d+(?:\.(\d+))?$/)
  if (match == null) {
    return null
  }
  // Group 1 is the optional minor; absent means legacy "vX" → minor 0.
  return match[1] != null ? Number(match[1]) : 0
}

/**
 * Find the latest version within a given major — the one with the highest
 * minor version. Used to resolve which version to load by default so the app
 * stays pinned to the supported major instead of the backend's global latest
 * (which may belong to a newer, unsupported major).
 *
 * @param versions - All available version strings (e.g. ["v2.3", "v1.4"]).
 * @param major - The major version to search within. Defaults to the UI's
 *   supported major (SUPPORTED_MAJOR_VERSION).
 * @returns The highest-minor version string within the major (e.g. "v2.3"), or
 *   null if no versions exist for that major.
 */
export function getLatestVersionForMajor(
  { versions, major = SUPPORTED_MAJOR_VERSION }: { versions: string[]; major?: number },
): string | null {
  const inMajor: string[] = getVersionsForMajor({ versions, major })
  if (inMajor.length === 0) {
    return null
  }
  // Pick the version with the highest minor. parseMinorVersion never returns
  // null here because getVersionsForMajor already matched the "vX" prefix.
  return inMajor.reduce((latest, candidate) => {
    const latestMinor: number = parseMinorVersion(latest) ?? 0
    const candidateMinor: number = parseMinorVersion(candidate) ?? 0
    return candidateMinor > latestMinor ? candidate : latest
  })
}

/**
 * Fetch all parasite rate data for a single country at a specific version.
 *
 * @param token - Auth0 access token for API authentication.
 * @param countryId - The ISO3 country identifier (e.g. "KEN").
 * @param version - Optional dataset version to query (e.g. "v2.3"). If omitted, the backend returns the latest.
 * @returns The country data response with column-oriented data.
 * @throws Error if the API call fails.
 */
export async function getCountryData(
  token: string,
  countryId: string,
  version?: string,
): Promise<CountryDataResponse> {
  const apiUrl = await loadApiUrl()

  const base = `${apiUrl}/countries/${encodeURIComponent(countryId)}`
  const url = version
    ? `${base}?version=${encodeURIComponent(version)}`
    : base
  const response = await fetch(url, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(
      errorData.error || `Failed to fetch data for country '${countryId}': HTTP ${response.status}`,
    )
  }

  return response.json()
}

/**
 * Fetch the source-type coverage for a single country at a specific version.
 *
 * @param token - Auth0 access token for API authentication.
 * @param countryId - The ISO3 country identifier (e.g. "KEN").
 * @param version - Optional dataset version to query (e.g. "v2.3"). If omitted, the backend returns the latest.
 * @returns The coverage response with the three per-year count maps.
 * @throws Error if the API call fails.
 */
export async function getCountryCoverage(
  token: string,
  countryId: string,
  version?: string,
): Promise<CountryCoverageResponse> {
  const apiUrl = await loadApiUrl()

  const base = `${apiUrl}/countries/${encodeURIComponent(countryId)}/coverage`
  const url = version
    ? `${base}?version=${encodeURIComponent(version)}`
    : base
  const response = await fetch(url, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(
      errorData.error || `Failed to fetch coverage for country '${countryId}': HTTP ${response.status}`,
    )
  }

  // Normalise the per-year maps at the API boundary. The current backend always
  // emits both categories, but an older or partially-populated deployment can
  // return a 200 whose body omits a category (e.g. a country with no literature
  // review points). A missing category means "zero data points of that kind",
  // not a malformed response, so we substitute an empty map here rather than
  // letting `undefined` propagate to consumers and crash on `map[year]`. The two
  // coerced fields (version, country_id) are passed through unchanged.
  const raw = (await response.json()) as Partial<CountryCoverageResponse>
  return {
    version: raw.version ?? "",
    country_id: raw.country_id ?? countryId,
    surveys: raw.surveys ?? {},
    literature_review: raw.literature_review ?? {},
  }
}

// ---------------------------------------------------------------------------
// Data Transformation
// ---------------------------------------------------------------------------

/**
 * Normalise a raw latitude/longitude pair into a plottable pair or null.
 *
 * A coordinate pair is kept only when BOTH values are present, numeric, and in
 * range; otherwise both become null (a half-valid pair is not a usable
 * location). Crucially this does NOT drop the row — coordinate-less rows are
 * retained so they remain queryable via the SQL feature; the map simply filters
 * them out of its layers.
 *
 * @param lat - The raw latitude cell value (any type / null / undefined).
 * @param lon - The raw longitude cell value (any type / null / undefined).
 * @returns An object with `latitude` and `longitude` as valid numbers, or both
 *   null when the pair is missing, non-numeric, or out of range.
 */
function normalizeCoordinates(
  lat: unknown,
  lon: unknown,
): { latitude: number | null; longitude: number | null } {
  const valid: boolean =
    typeof lat === "number" && typeof lon === "number" &&
    lat >= -90 && lat <= 90 && lon >= -180 && lon <= 180
  return {
    latitude: valid ? (lat as number) : null,
    longitude: valid ? (lon as number) : null,
  }
}

/**
 * Convert column-oriented response data into an array of row-oriented
 * ParasiteRatePoint records containing all columns.
 *
 * Every row is kept. A row whose latitude/longitude is missing, non-numeric, or
 * out of range has both coordinates normalised to null (see
 * {@link normalizeCoordinates}) but is still included, so coordinate-less rows
 * remain available to the SQL query feature. The map plots only the rows that
 * have coordinates.
 *
 * @param data - Column-oriented data from the API response.
 * @param rowCount - Number of rows in the dataset.
 * @returns Array of ParasiteRatePoint records (one per row).
 */
function columnsToPoints(
  data: Record<string, (string | number | null)[]>,
  rowCount: number,
): ParasiteRatePoint[] {
  const points: ParasiteRatePoint[] = []

  for (let i = 0; i < rowCount; i++) {
    const lat = data["latitude"]?.[i]
    const lon = data["longitude"]?.[i]

    const { latitude: normalizedLat, longitude: normalizedLon } = normalizeCoordinates(lat, lon)

    points.push({
      country: (data["country"]?.[i] as string) ?? null,
      country_id: (data["country_id"]?.[i] as string) ?? null,
      cluster_id: (data["cluster_id"]?.[i] as string) ?? null,
      site_name: (data["site_name"]?.[i] as string) ?? null,
      dhs_survey_num: (data["dhs_survey_num"]?.[i] as string) ?? null,
      dhs_survey_id: (data["dhs_survey_id"]?.[i] as string) ?? null,
      dhs_id: (data["dhs_id"]?.[i] as string) ?? null,
      latitude: normalizedLat,
      longitude: normalizedLon,
      rural_urban_type: (data["rural_urban_type"]?.[i] as string) ?? null,
      extracted_admin_1_name: (data["extracted_admin_1_name"]?.[i] as string) ?? null,
      admin1_dhs: (data["admin1_dhs"]?.[i] as string) ?? null,
      dhs_region_code: (data["dhs_region_code"]?.[i] as string) ?? null,
      dhs_region_name: (data["dhs_region_name"]?.[i] as string) ?? null,
      lat_long_source: (data["lat_long_source"]?.[i] as string) ?? null,
      area_type: (data["area_type"]?.[i] as string) ?? null,
      area_size: (data["area_size"]?.[i] as string) ?? null,
      is_school: (data["is_school"]?.[i] as string) ?? null,
      is_excluded: (data["is_excluded"]?.[i] as string) ?? null,
      exclusion_reason: (data["exclusion_reason"]?.[i] as string) ?? null,
      survey_notes: (data["survey_notes"]?.[i] as string) ?? null,
      site_notes: (data["site_notes"]?.[i] as string) ?? null,
      pcr_type: (data["pcr_type"]?.[i] as string) ?? null,
      rdt_type: (data["rdt_type"]?.[i] as string) ?? null,
      micro_type: (data["micro_type"]?.[i] as string) ?? null,
      method: (data["method"]?.[i] as string) ?? null,
      household_sample_weight: (data["household_sample_weight"]?.[i] as number) ?? null,
      month_start: (data["month_start"]?.[i] as number) ?? null,
      month_end: (data["month_end"]?.[i] as number) ?? null,
      day_start: (data["day_start"]?.[i] as number) ?? null,
      day_end: (data["day_end"]?.[i] as number) ?? null,
      year_start: (data["year_start"]?.[i] as number) ?? null,
      year_end: (data["year_end"]?.[i] as number) ?? null,
      lower_age_months_rounded: (data["lower_age_months_rounded"]?.[i] as number) ?? null,
      upper_age_months_rounded: (data["upper_age_months_rounded"]?.[i] as number) ?? null,
      examined: (data["examined"]?.[i] as number) ?? null,
      any_positive_count: (data["any_positive_count"]?.[i] as number) ?? null,
      any_positive_rate: (data["any_positive_rate"]?.[i] as number) ?? null,
      pf_positive_count: (data["pf_positive_count"]?.[i] as number) ?? null,
      pf_positive_rate: (data["pf_positive_rate"]?.[i] as number) ?? null,
      computed_total_pf_positive_count: (data["computed_total_pf_positive_count"]?.[i] as number) ?? null,
      computed_total_pf_positive_rate: (data["computed_total_pf_positive_rate"]?.[i] as number) ?? null,
      pv_positive_count: (data["pv_positive_count"]?.[i] as number) ?? null,
      pv_positive_rate: (data["pv_positive_rate"]?.[i] as number) ?? null,
      computed_total_pv_positive_count: (data["computed_total_pv_positive_count"]?.[i] as number) ?? null,
      computed_total_pv_positive_rate: (data["computed_total_pv_positive_rate"]?.[i] as number) ?? null,
      pk_positive_count: (data["pk_positive_count"]?.[i] as number) ?? null,
      pk_positive_rate: (data["pk_positive_rate"]?.[i] as number) ?? null,
      computed_total_pk_positive_count: (data["computed_total_pk_positive_count"]?.[i] as number) ?? null,
      computed_total_pk_positive_rate: (data["computed_total_pk_positive_rate"]?.[i] as number) ?? null,
      po_positive_count: (data["po_positive_count"]?.[i] as number) ?? null,
      po_positive_rate: (data["po_positive_rate"]?.[i] as number) ?? null,
      computed_total_po_positive_count: (data["computed_total_po_positive_count"]?.[i] as number) ?? null,
      computed_total_po_positive_rate: (data["computed_total_po_positive_rate"]?.[i] as number) ?? null,
      pm_positive_count: (data["pm_positive_count"]?.[i] as number) ?? null,
      pm_positive_rate: (data["pm_positive_rate"]?.[i] as number) ?? null,
      computed_total_pm_positive_count: (data["computed_total_pm_positive_count"]?.[i] as number) ?? null,
      computed_total_pm_positive_rate: (data["computed_total_pm_positive_rate"]?.[i] as number) ?? null,
      pan_positive_count: (data["pan_positive_count"]?.[i] as number) ?? null,
      pan_positive_rate: (data["pan_positive_rate"]?.[i] as number) ?? null,
      pf_pv_positive_count: (data["pf_pv_positive_count"]?.[i] as number) ?? null,
      pf_pv_positive_rate: (data["pf_pv_positive_rate"]?.[i] as number) ?? null,
      pf_po_positive_count: (data["pf_po_positive_count"]?.[i] as number) ?? null,
      pf_po_positive_rate: (data["pf_po_positive_rate"]?.[i] as number) ?? null,
      pf_pm_positive_count: (data["pf_pm_positive_count"]?.[i] as number) ?? null,
      pf_pm_positive_rate: (data["pf_pm_positive_rate"]?.[i] as number) ?? null,
      pv_pm_positive_count: (data["pv_pm_positive_count"]?.[i] as number) ?? null,
      pv_pm_positive_rate: (data["pv_pm_positive_rate"]?.[i] as number) ?? null,
      pv_po_positive_count: (data["pv_po_positive_count"]?.[i] as number) ?? null,
      pv_po_positive_rate: (data["pv_po_positive_rate"]?.[i] as number) ?? null,
      po_pm_positive_count: (data["po_pm_positive_count"]?.[i] as number) ?? null,
      po_pm_positive_rate: (data["po_pm_positive_rate"]?.[i] as number) ?? null,
      pk_pf_positive_count: (data["pk_pf_positive_count"]?.[i] as number) ?? null,
      pk_pf_positive_rate: (data["pk_pf_positive_rate"]?.[i] as number) ?? null,
      pk_pv_positive_count: (data["pk_pv_positive_count"]?.[i] as number) ?? null,
      pk_pv_positive_rate: (data["pk_pv_positive_rate"]?.[i] as number) ?? null,
      pk_pm_positive_count: (data["pk_pm_positive_count"]?.[i] as number) ?? null,
      pk_pm_positive_rate: (data["pk_pm_positive_rate"]?.[i] as number) ?? null,
      pk_po_positive_count: (data["pk_po_positive_count"]?.[i] as number) ?? null,
      pk_po_positive_rate: (data["pk_po_positive_rate"]?.[i] as number) ?? null,
      pf_pv_po_positive_count: (data["pf_pv_po_positive_count"]?.[i] as number) ?? null,
      pf_pv_po_positive_rate: (data["pf_pv_po_positive_rate"]?.[i] as number) ?? null,
      pf_pv_pm_positive_count: (data["pf_pv_pm_positive_count"]?.[i] as number) ?? null,
      pf_pv_pm_positive_rate: (data["pf_pv_pm_positive_rate"]?.[i] as number) ?? null,
      pf_po_pm_positive_count: (data["pf_po_pm_positive_count"]?.[i] as number) ?? null,
      pf_po_pm_positive_rate: (data["pf_po_pm_positive_rate"]?.[i] as number) ?? null,
      pv_po_pm_positive_count: (data["pv_po_pm_positive_count"]?.[i] as number) ?? null,
      pv_po_pm_positive_rate: (data["pv_po_pm_positive_rate"]?.[i] as number) ?? null,
      pf_pv_pk_positive_count: (data["pf_pv_pk_positive_count"]?.[i] as number) ?? null,
      pf_pv_pk_positive_rate: (data["pf_pv_pk_positive_rate"]?.[i] as number) ?? null,
      pf_po_pk_positive_count: (data["pf_po_pk_positive_count"]?.[i] as number) ?? null,
      pf_po_pk_positive_rate: (data["pf_po_pk_positive_rate"]?.[i] as number) ?? null,
      pf_pm_pk_positive_count: (data["pf_pm_pk_positive_count"]?.[i] as number) ?? null,
      pf_pm_pk_positive_rate: (data["pf_pm_pk_positive_rate"]?.[i] as number) ?? null,
      pv_po_pk_positive_count: (data["pv_po_pk_positive_count"]?.[i] as number) ?? null,
      pv_po_pk_positive_rate: (data["pv_po_pk_positive_rate"]?.[i] as number) ?? null,
      pv_pm_pk_positive_count: (data["pv_pm_pk_positive_count"]?.[i] as number) ?? null,
      pv_pm_pk_positive_rate: (data["pv_pm_pk_positive_rate"]?.[i] as number) ?? null,
      po_pm_pk_positive_count: (data["po_pm_pk_positive_count"]?.[i] as number) ?? null,
      po_pm_pk_positive_rate: (data["po_pm_pk_positive_rate"]?.[i] as number) ?? null,
      pf_pv_po_pm_positive_count: (data["pf_pv_po_pm_positive_count"]?.[i] as number) ?? null,
      pf_pv_po_pm_positive_rate: (data["pf_pv_po_pm_positive_rate"]?.[i] as number) ?? null,
      pf_pan_positive_count: (data["pf_pan_positive_count"]?.[i] as number) ?? null,
      pf_pan_positive_rate: (data["pf_pan_positive_rate"]?.[i] as number) ?? null,
      pv_pan_positive_count: (data["pv_pan_positive_count"]?.[i] as number) ?? null,
      pv_pan_positive_rate: (data["pv_pan_positive_rate"]?.[i] as number) ?? null,
      pf_pv_pan_positive_count: (data["pf_pv_pan_positive_count"]?.[i] as number) ?? null,
      pf_pv_pan_positive_rate: (data["pf_pv_pan_positive_rate"]?.[i] as number) ?? null,
      map_survey_id: (data["map_survey_id"]?.[i] as string) ?? null,
      source_id1: (data["source_id1"]?.[i] != null) ? String(data["source_id1"][i]) : null,
      lit_review_id: (data["lit_review_id"]?.[i] as string) ?? null,
      extracted_admin_2_name: (data["extracted_admin_2_name"]?.[i] as string) ?? null,
      extracted_admin_3_name: (data["extracted_admin_3_name"]?.[i] as string) ?? null,
      source_id2: (data["source_id2"]?.[i] as string) ?? null,
      source_id3: (data["source_id3"]?.[i] as string) ?? null,
    })
  }

  return points
}

// ---------------------------------------------------------------------------
// Aggregated Data Loader (with version-based caching)
// ---------------------------------------------------------------------------

/**
 * In-memory cache for parasite rate data. Stores the loaded points keyed by
 * the dataset version string. If the version hasn't changed since last load,
 * the cached data is returned immediately without re-fetching.
 */
let _cachedVersion: string | null = null
let _cachedPoints: ParasiteRatePoint[] = []

/**
 * Load all parasite rate data across all countries, with version-based caching.
 * Fires all country requests concurrently and reports progress as each one
 * completes via an optional callback.
 *
 * @param token - Auth0 access token for API authentication.
 * @param version - Optional dataset version to load (e.g. "v2.3"). If omitted, the backend returns the latest.
 * @param onProgress - Optional callback invoked each time a country completes,
 *   with (completedCount, totalCountries).
 * @returns An array of all ParasiteRatePoint records across all countries.
 * @throws Error if the countries endpoint fails. Individual country failures
 *   are logged but do not prevent other countries from loading.
 */
export async function loadAllParasiteRateData(
  token: string,
  version?: string,
  onProgress?: (completed: number, total: number) => void,
): Promise<ParasiteRatePoint[]> {
  // Step 1: Get the list of countries with data for the specified version
  const countriesResponse = await getCountries(token, version)
  const { countries, version: resolvedVersion } = countriesResponse

  // Step 2: Return cached data if the version hasn't changed
  if (resolvedVersion === _cachedVersion && _cachedPoints.length > 0) {
    onProgress?.(countries.length, countries.length)
    return _cachedPoints
  }

  if (countries.length === 0) {
    _cachedVersion = resolvedVersion
    _cachedPoints = []
    onProgress?.(0, 0)
    return []
  }

  // Step 3: Fire all requests concurrently, track progress
  const allPoints: ParasiteRatePoint[] = []
  let completedCount = 0
  const total = countries.length

  onProgress?.(0, total)

  await new Promise<void>((resolve) => {
    for (const countryId of countries) {
      getCountryData(token, countryId, version)
        .then((response) => {
          const { data, row_count } = response
          const points = columnsToPoints(data, row_count)
          allPoints.push(...points)
        })
        .catch((err) => {
          console.error(`Failed to load country data for '${countryId}':`, err)
        })
        .finally(() => {
          completedCount++
          onProgress?.(completedCount, total)
          if (completedCount === total) {
            resolve()
          }
        })
    }
  })

  // Step 4: Cache the result keyed by version
  _cachedVersion = resolvedVersion
  _cachedPoints = allPoints

  return allPoints
}

// ---------------------------------------------------------------------------
// Validation Failures Loader
// ---------------------------------------------------------------------------

/**
 * Response shape from GET /api/parasite-rate/validation-failures.
 * Uses the same column-oriented format as country data responses.
 */
interface ValidationFailuresResponse {
  /** The resolved dataset version. */
  version: string
  /** Number of rows in the data. */
  row_count: number
  /** Column-oriented data (column_name → values[]). */
  data: Record<string, (string | number | null)[]>
}

/**
 * Fetch validation failure records from the API for a specific version.
 *
 * @param token - Auth0 access token for API authentication.
 * @param version - Optional dataset version to query (e.g. "v2.3"). If omitted, the backend returns the latest.
 * @returns The validation failures response with column-oriented data.
 * @throws Error if the API call fails.
 */
export async function getValidationFailures(token: string, version?: string): Promise<ValidationFailuresResponse> {
  const apiUrl = await loadApiUrl()

  const base = `${apiUrl}/validation-failures`
  const url = version
    ? `${base}?version=${encodeURIComponent(version)}`
    : base
  const response = await fetch(url, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(
      errorData.error || `Failed to fetch validation failures: HTTP ${response.status}`,
    )
  }

  return response.json()
}

/**
 * Load validation failure data and convert it into ValidationFailurePoint
 * records. Uses the same column-to-row conversion as the main data, with
 * the addition of the `validation_error` column.
 *
 * @param token - Auth0 access token for API authentication.
 * @param version - Optional dataset version to query (e.g. "v2.3"). If omitted, the backend returns the latest.
 * @returns An array of ValidationFailurePoint records with coordinates.
 * @throws Error if the API call fails.
 */
export async function loadValidationFailures(token: string, version?: string): Promise<ValidationFailurePoint[]> {
  const response = await getValidationFailures(token, version)
  const { data, row_count } = response

  if (row_count === 0) {
    return []
  }

  // Built row-by-row (rather than delegating to columnsToPoints) because each
  // record additionally carries the validation_error column. Every row is kept;
  // coordinates are normalised the same way as columnsToPoints.
  const points: ValidationFailurePoint[] = []

  for (let i = 0; i < row_count; i++) {
    const lat = data["latitude"]?.[i]
    const lon = data["longitude"]?.[i]

    const { latitude: normalizedLat, longitude: normalizedLon } = normalizeCoordinates(lat, lon)

    points.push({
      country: (data["country"]?.[i] as string) ?? null,
      country_id: (data["country_id"]?.[i] as string) ?? null,
      cluster_id: (data["cluster_id"]?.[i] as string) ?? null,
      site_name: (data["site_name"]?.[i] as string) ?? null,
      dhs_survey_num: (data["dhs_survey_num"]?.[i] as string) ?? null,
      dhs_survey_id: (data["dhs_survey_id"]?.[i] as string) ?? null,
      dhs_id: (data["dhs_id"]?.[i] as string) ?? null,
      latitude: normalizedLat,
      longitude: normalizedLon,
      rural_urban_type: (data["rural_urban_type"]?.[i] as string) ?? null,
      extracted_admin_1_name: (data["extracted_admin_1_name"]?.[i] as string) ?? null,
      admin1_dhs: (data["admin1_dhs"]?.[i] as string) ?? null,
      dhs_region_code: (data["dhs_region_code"]?.[i] as string) ?? null,
      dhs_region_name: (data["dhs_region_name"]?.[i] as string) ?? null,
      lat_long_source: (data["lat_long_source"]?.[i] as string) ?? null,
      area_type: (data["area_type"]?.[i] as string) ?? null,
      area_size: (data["area_size"]?.[i] as string) ?? null,
      is_school: (data["is_school"]?.[i] as string) ?? null,
      is_excluded: (data["is_excluded"]?.[i] as string) ?? null,
      exclusion_reason: (data["exclusion_reason"]?.[i] as string) ?? null,
      survey_notes: (data["survey_notes"]?.[i] as string) ?? null,
      site_notes: (data["site_notes"]?.[i] as string) ?? null,
      pcr_type: (data["pcr_type"]?.[i] as string) ?? null,
      rdt_type: (data["rdt_type"]?.[i] as string) ?? null,
      micro_type: (data["micro_type"]?.[i] as string) ?? null,
      method: (data["method"]?.[i] as string) ?? null,
      household_sample_weight: (data["household_sample_weight"]?.[i] as number) ?? null,
      month_start: (data["month_start"]?.[i] as number) ?? null,
      month_end: (data["month_end"]?.[i] as number) ?? null,
      day_start: (data["day_start"]?.[i] as number) ?? null,
      day_end: (data["day_end"]?.[i] as number) ?? null,
      year_start: (data["year_start"]?.[i] as number) ?? null,
      year_end: (data["year_end"]?.[i] as number) ?? null,
      lower_age_months_rounded: (data["lower_age_months_rounded"]?.[i] as number) ?? null,
      upper_age_months_rounded: (data["upper_age_months_rounded"]?.[i] as number) ?? null,
      examined: (data["examined"]?.[i] as number) ?? null,
      any_positive_count: (data["any_positive_count"]?.[i] as number) ?? null,
      any_positive_rate: (data["any_positive_rate"]?.[i] as number) ?? null,
      pf_positive_count: (data["pf_positive_count"]?.[i] as number) ?? null,
      pf_positive_rate: (data["pf_positive_rate"]?.[i] as number) ?? null,
      computed_total_pf_positive_count: (data["computed_total_pf_positive_count"]?.[i] as number) ?? null,
      computed_total_pf_positive_rate: (data["computed_total_pf_positive_rate"]?.[i] as number) ?? null,
      pv_positive_count: (data["pv_positive_count"]?.[i] as number) ?? null,
      pv_positive_rate: (data["pv_positive_rate"]?.[i] as number) ?? null,
      computed_total_pv_positive_count: (data["computed_total_pv_positive_count"]?.[i] as number) ?? null,
      computed_total_pv_positive_rate: (data["computed_total_pv_positive_rate"]?.[i] as number) ?? null,
      pk_positive_count: (data["pk_positive_count"]?.[i] as number) ?? null,
      pk_positive_rate: (data["pk_positive_rate"]?.[i] as number) ?? null,
      computed_total_pk_positive_count: (data["computed_total_pk_positive_count"]?.[i] as number) ?? null,
      computed_total_pk_positive_rate: (data["computed_total_pk_positive_rate"]?.[i] as number) ?? null,
      po_positive_count: (data["po_positive_count"]?.[i] as number) ?? null,
      po_positive_rate: (data["po_positive_rate"]?.[i] as number) ?? null,
      computed_total_po_positive_count: (data["computed_total_po_positive_count"]?.[i] as number) ?? null,
      computed_total_po_positive_rate: (data["computed_total_po_positive_rate"]?.[i] as number) ?? null,
      pm_positive_count: (data["pm_positive_count"]?.[i] as number) ?? null,
      pm_positive_rate: (data["pm_positive_rate"]?.[i] as number) ?? null,
      computed_total_pm_positive_count: (data["computed_total_pm_positive_count"]?.[i] as number) ?? null,
      computed_total_pm_positive_rate: (data["computed_total_pm_positive_rate"]?.[i] as number) ?? null,
      pan_positive_count: (data["pan_positive_count"]?.[i] as number) ?? null,
      pan_positive_rate: (data["pan_positive_rate"]?.[i] as number) ?? null,
      pf_pv_positive_count: (data["pf_pv_positive_count"]?.[i] as number) ?? null,
      pf_pv_positive_rate: (data["pf_pv_positive_rate"]?.[i] as number) ?? null,
      pf_po_positive_count: (data["pf_po_positive_count"]?.[i] as number) ?? null,
      pf_po_positive_rate: (data["pf_po_positive_rate"]?.[i] as number) ?? null,
      pf_pm_positive_count: (data["pf_pm_positive_count"]?.[i] as number) ?? null,
      pf_pm_positive_rate: (data["pf_pm_positive_rate"]?.[i] as number) ?? null,
      pv_pm_positive_count: (data["pv_pm_positive_count"]?.[i] as number) ?? null,
      pv_pm_positive_rate: (data["pv_pm_positive_rate"]?.[i] as number) ?? null,
      pv_po_positive_count: (data["pv_po_positive_count"]?.[i] as number) ?? null,
      pv_po_positive_rate: (data["pv_po_positive_rate"]?.[i] as number) ?? null,
      po_pm_positive_count: (data["po_pm_positive_count"]?.[i] as number) ?? null,
      po_pm_positive_rate: (data["po_pm_positive_rate"]?.[i] as number) ?? null,
      pk_pf_positive_count: (data["pk_pf_positive_count"]?.[i] as number) ?? null,
      pk_pf_positive_rate: (data["pk_pf_positive_rate"]?.[i] as number) ?? null,
      pk_pv_positive_count: (data["pk_pv_positive_count"]?.[i] as number) ?? null,
      pk_pv_positive_rate: (data["pk_pv_positive_rate"]?.[i] as number) ?? null,
      pk_pm_positive_count: (data["pk_pm_positive_count"]?.[i] as number) ?? null,
      pk_pm_positive_rate: (data["pk_pm_positive_rate"]?.[i] as number) ?? null,
      pk_po_positive_count: (data["pk_po_positive_count"]?.[i] as number) ?? null,
      pk_po_positive_rate: (data["pk_po_positive_rate"]?.[i] as number) ?? null,
      pf_pv_po_positive_count: (data["pf_pv_po_positive_count"]?.[i] as number) ?? null,
      pf_pv_po_positive_rate: (data["pf_pv_po_positive_rate"]?.[i] as number) ?? null,
      pf_pv_pm_positive_count: (data["pf_pv_pm_positive_count"]?.[i] as number) ?? null,
      pf_pv_pm_positive_rate: (data["pf_pv_pm_positive_rate"]?.[i] as number) ?? null,
      pf_po_pm_positive_count: (data["pf_po_pm_positive_count"]?.[i] as number) ?? null,
      pf_po_pm_positive_rate: (data["pf_po_pm_positive_rate"]?.[i] as number) ?? null,
      pv_po_pm_positive_count: (data["pv_po_pm_positive_count"]?.[i] as number) ?? null,
      pv_po_pm_positive_rate: (data["pv_po_pm_positive_rate"]?.[i] as number) ?? null,
      pf_pv_pk_positive_count: (data["pf_pv_pk_positive_count"]?.[i] as number) ?? null,
      pf_pv_pk_positive_rate: (data["pf_pv_pk_positive_rate"]?.[i] as number) ?? null,
      pf_po_pk_positive_count: (data["pf_po_pk_positive_count"]?.[i] as number) ?? null,
      pf_po_pk_positive_rate: (data["pf_po_pk_positive_rate"]?.[i] as number) ?? null,
      pf_pm_pk_positive_count: (data["pf_pm_pk_positive_count"]?.[i] as number) ?? null,
      pf_pm_pk_positive_rate: (data["pf_pm_pk_positive_rate"]?.[i] as number) ?? null,
      pv_po_pk_positive_count: (data["pv_po_pk_positive_count"]?.[i] as number) ?? null,
      pv_po_pk_positive_rate: (data["pv_po_pk_positive_rate"]?.[i] as number) ?? null,
      pv_pm_pk_positive_count: (data["pv_pm_pk_positive_count"]?.[i] as number) ?? null,
      pv_pm_pk_positive_rate: (data["pv_pm_pk_positive_rate"]?.[i] as number) ?? null,
      po_pm_pk_positive_count: (data["po_pm_pk_positive_count"]?.[i] as number) ?? null,
      po_pm_pk_positive_rate: (data["po_pm_pk_positive_rate"]?.[i] as number) ?? null,
      pf_pv_po_pm_positive_count: (data["pf_pv_po_pm_positive_count"]?.[i] as number) ?? null,
      pf_pv_po_pm_positive_rate: (data["pf_pv_po_pm_positive_rate"]?.[i] as number) ?? null,
      pf_pan_positive_count: (data["pf_pan_positive_count"]?.[i] as number) ?? null,
      pf_pan_positive_rate: (data["pf_pan_positive_rate"]?.[i] as number) ?? null,
      pv_pan_positive_count: (data["pv_pan_positive_count"]?.[i] as number) ?? null,
      pv_pan_positive_rate: (data["pv_pan_positive_rate"]?.[i] as number) ?? null,
      pf_pv_pan_positive_count: (data["pf_pv_pan_positive_count"]?.[i] as number) ?? null,
      pf_pv_pan_positive_rate: (data["pf_pv_pan_positive_rate"]?.[i] as number) ?? null,
      map_survey_id: (data["map_survey_id"]?.[i] as string) ?? null,
      source_id1: (data["source_id1"]?.[i] != null) ? String(data["source_id1"][i]) : null,
      lit_review_id: (data["lit_review_id"]?.[i] as string) ?? null,
      extracted_admin_2_name: (data["extracted_admin_2_name"]?.[i] as string) ?? null,
      extracted_admin_3_name: (data["extracted_admin_3_name"]?.[i] as string) ?? null,
      source_id2: (data["source_id2"]?.[i] as string) ?? null,
      source_id3: (data["source_id3"]?.[i] as string) ?? null,
      validation_error: (data["validation_error"]?.[i] as string) ?? null,
    })
  }

  return points
}